import { InputChecker } from "@/src/components/formValidation";
import { MySelect } from "@/src/components/mySelect";
import { UploadSpot, UploadSpots } from "@/src/components/uploadSpots";
import { openToast } from "@/src/utils/toasts";
import { uid } from "@/src/utils/utils";
import moment from "moment";
import { forwardRef, useEffect, useRef, useState } from "react";
import { AiDescription } from "../app/aiTasks/aiDescription";
import { HtmlArea } from "../app/documents/documentEditor";
import { InputsType, Patient } from "../data/allTypes";
import { CornerIconButton } from "./buttons";
import { closeDialog, openDialog } from "./dialogs";
import { TextHistoryButton } from "./textHistoryButton";
import { AiEditWrapper, FieldAnswerAiEdit } from "../app/aiTasks/AiEditWrapper";

export function Input({
  ref,
  onRefSet,
  id,
  shouldFocus,
  name,
  additionalUploadData,
  isHorizontal,
  showHistoryButton,
  isRequired,
  isCreatable,
  value,
  type,
  unitName,
  placeholder,
  onKeyUp,
  onEnter,
  rows,
  options,
  onChange,
  onBlur,
  aiEdit,
}: {
  id?: string | null | undefined;
  ref?: React.RefObject<HTMLInputElement>;
  onRefSet?: (ref: React.RefObject<HTMLInputElement>) => void;
  shouldFocus?: boolean;
  name: string | null | undefined;
  additionalUploadData?: Record<string, any>;
  value: any;
  patient?: Patient | null | undefined;
  showHistoryButton?: boolean;
  isHorizontal?: boolean;
  isRequired?: boolean;
  isCreatable?: boolean;
  type: InputsType;
  rows?: number;
  unitName?: string;
  placeholder?: string;
  options?:
    | {
        value: string | null | undefined;
        label: string | null | undefined;
      }[]
    | {
        id: string | null | undefined;
        name: string | null | undefined;
      }[];
  onKeyUp?: (e: any) => void;
  onEnter?: (e: any) => void;
  onChange: (newValue: any) => void;
  onBlur?: (newValue: any) => void;
  aiEdit?: FieldAnswerAiEdit | null | undefined;
}) {
  let [localValue, setLocalValue] = useState(value);
  return (
    <UniversalInput
      id={id}
      ref={ref}
      onRefSet={onRefSet}
      shouldFocus={shouldFocus}
      isHorizontal={isHorizontal}
      name={name}
      additionalUploadData={additionalUploadData}
      isRequired={isRequired}
      isCreatable={isCreatable}
      value={localValue}
      type={type}
      unitName={unitName}
      placeholder={placeholder}
      onKeyUp={onKeyUp}
      onEnter={onEnter}
      rows={rows}
      options={options}
      onChange={onChange}
      onBlur={onBlur}
      aiEdit={aiEdit}
      labelTrailing={
        (type == "textarea" || type == "htmlarea") &&
        showHistoryButton !== false && (
          <TextHistoryButton
            inputId={name || ""}
            currentText={localValue}
            onClick={(newValue) => {}}
            onSelect={(newValue) => {
              // let position = inputRef?.current?.selectionStart || 0;
              let position = 0;

              let newStr =
                localValue.slice(0, position) +
                newValue +
                localValue.slice(position);

              setLocalValue(newStr);
              onChange?.(newStr);
            }}
          ></TextHistoryButton>
        )
      }
    />
  );
}
export const UniversalInput = forwardRef<
  HTMLElement,
  {
    id?: string | null | undefined;
    ref?: React.RefObject<any>;
    onRefSet?: (ref: React.RefObject<any>) => void;
    shouldFocus?: boolean;
    isHorizontal?: boolean;
    isCreatable?: boolean;
    type: InputsType;
    name?: string | null | undefined;
    value: any;
    isRequired?: boolean;
    placeholder?: string;
    options?:
      | {
          value: string | null | undefined;
          label: string | null | undefined;
        }[]
      | {
          id: string | null | undefined;
          name: string | null | undefined;
        }[]
      | string[];
    labelTrailing?: any;
    additionalUploadData?: Record<string, any>;
    rows?: number;
    unitName?: string;
    onChange: (newValue: any) => void;
    onKeyUp?: (e: any) => void;
    onEnter?: (e: any) => void;
    onBlur?: (newValue: any) => void;
    aiEdit?: FieldAnswerAiEdit | null | undefined;
  }
>(function UniversalInput(
  {
    value,
    options,
    shouldFocus,
    onRefSet,
    onKeyUp,
    onEnter,
    onBlur,
    id,
    name,
    isRequired,
    isCreatable,
    type,
    unitName,
    placeholder,
    rows,
    labelTrailing,
    additionalUploadData,
    isHorizontal,
    aiEdit,
    onChange,
  },
  ref
) {
  const [localAiEdit, setLocalAiEdit] = useState(aiEdit);
  const [hasChanged, setHasChanged] = useState(false);
  let [localValue, setLocalValue] = useState(value);
  const [localOptions, setLocalOptions] = useState(options || []);
  const inputRef = useRef<any>(ref);

  ref = inputRef;
  useEffect(() => {
    if (shouldFocus && inputRef.current) inputRef.current.focus();
  }, [shouldFocus]);

  useEffect(() => {
    const inputElem = inputRef?.current;
    onRefSet?.(inputRef);
    const handleWheel = (e: any) => e.preventDefault();
    inputElem?.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      inputElem?.removeEventListener("wheel", handleWheel);
    };
  }, []);

  useEffect(() => {
    onRefSet?.(inputRef);
  }, [inputRef.current]);

  async function localOnKeyUp(e: any) {
    try {
      await onKeyUp?.(e);
      if (e.key === "Enter") {
        onEnter?.(e);
      }
    } catch (e: any) {
      openToast("error", e?.message || e?.toString());
    }
  }

  function onInputChange(newValue: any) {
    setLocalValue(newValue);
    onChange?.(newValue);
    setHasChanged(true);
    setLocalAiEdit(null);
  }

  function onInputBlur(e: any) {
    onBlur?.(localValue);
  }

  if (type == "title") return <div className={`f16 bold`}>{name}</div>;

  if (type == "subtitle") return <div className={`f14 bold`}>{name}</div>;

  return (
    <InputChecker
      id={id}
      name={name}
      value={localValue}
      isRequired={isRequired}
      hasChanged={hasChanged}
    >
      <AiEditWrapper
        aiEdit={localAiEdit}
        onUndo={() => {
          setLocalValue(localAiEdit?.oldValue);
          setLocalAiEdit(null);
          onChange?.(localAiEdit?.oldValue);
        }}
        onValidate={() => {
          setLocalAiEdit(null);
        }}
      >
        <AiDescription
          key={`${name}-${type}-${localValue}-${JSON.stringify(localOptions)}`}
          item={{
            type,
            title: name,
            value: localValue,
            placeholder,
            description: () => {
              return `A ${type} input for the ${name} field that allows the user to enter a value. The current value of the input is ${
                typeof localValue == "object"
                  ? JSON.stringify(localValue)
                  : localValue
              }.`;
            },
            options: localOptions.map((option) => {
              if (typeof option == "string") {
                option = {
                  value: option,
                  label: option,
                };
              }
              if ("id" in option) {
                return {
                  value: option.id,
                  label: option.name,
                };
              }
              return option;
            }),
            unitName,
            onChange: (newValue: any) => {
              onInputChange(newValue);
            },
            ref: inputRef,
          }}
        >
          <></>
        </AiDescription>

        <div
          className={`field ${unitName ? "hasFieldUnit" : ""} ${
            isHorizontal ? "horizontal" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {name != null && name != "" ? (
            <div className="label">
              <div>
                {name}
                {isRequired == true && typeof name == "string" ? "*" : ""}
              </div>
              {labelTrailing}
            </div>
          ) : null}

          <div className={`c1-auto relative ${name ? "" : "double"}`}>
            {(type == "select" || type == "select-multiple") && (
              <MySelect
                ref={inputRef}
                isMultiple={type == "select-multiple"}
                isCreatable={isCreatable}
                options={localOptions.map((option) => {
                  if (typeof option == "string") {
                    option = {
                      value: option,
                      label: option,
                    };
                  }
                  if ("id" in option) {
                    return {
                      value: option.id,
                      label: option.name,
                    };
                  }
                  return option;
                })}
                value={localValue}
                placeholder={placeholder}
                onChange={(newValue) => {
                  console.log("newvalue", newValue);
                  setLocalValue(newValue);
                  onChange?.(newValue);
                }}
              />
            )}

            {type == "dialogSelect" && (
              <DialogSelect
                ref={inputRef}
                gridCount={3}
                options={localOptions.map((option) => {
                  if (typeof option == "string") {
                    option = {
                      value: option,
                      label: option,
                    };
                  }

                  if ("id" in option) {
                    return {
                      value: option.id,
                      label: option.name,
                    };
                  }
                  return {
                    value: option.value,
                    label: option.label,
                  };
                })}
                current={localValue}
                onChange={(newValue) => {
                  setLocalValue(newValue);
                  onChange?.(newValue);
                }}
              ></DialogSelect>
            )}
            {type == "text" && (
              <input
                ref={inputRef}
                value={localValue}
                type="text"
                placeholder={placeholder}
                onChange={(e) => onInputChange(e?.target?.value)}
                onBlur={onInputBlur}
                onKeyUp={localOnKeyUp}
              />
            )}

            {type == "number" && (
              <input
                ref={inputRef}
                value={localValue}
                type="number"
                onWheel={(e) => {
                  try {
                    e.preventDefault();
                  } catch (e) {
                    console.log("e", e);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    onKeyUp?.(e);
                    e.preventDefault();
                  }
                }}
                placeholder={placeholder}
                onChange={(e) => onInputChange(e?.target?.value)}
                onBlur={onInputBlur}
                onKeyUp={localOnKeyUp}
              />
            )}

            {type == "password" && (
              <input
                value={localValue}
                type="password"
                placeholder={placeholder}
                onChange={(e) => onInputChange(e?.target?.value)}
                onBlur={onInputBlur}
                onKeyUp={localOnKeyUp}
              />
            )}
            {type == "uploads" && (
              <UploadSpots
                gridCount={2}
                spotsCount={2}
                additionalUploadData={additionalUploadData}
                value={localValue}
                onChange={(newValue) => {
                  onChange?.(newValue);
                  setLocalValue(newValue);
                }}
              />
            )}
            {type == "upload" && (
              <UploadSpot
                additionalUploadData={additionalUploadData}
                value={localValue}
                onChange={(newValue) => {
                  onChange?.(newValue);
                  setLocalValue(newValue);
                }}
              />
            )}

            {type == "htmlarea" && (
              <HtmlArea
                key={`${name}${localValue}`}
                ref={inputRef}
                value={localValue}
                placeholder={placeholder}
                rows={rows}
                onChange={(newValue) => {
                  // setLocalValue(newValue);
                  localValue = newValue;
                  // setLocalValue(newValue);
                  onChange?.(newValue);
                  // setHasChanged(true);
                }}
                onBlur={onInputBlur}
                onKeyUp={(newValue) => {
                  localValue = newValue;
                  // setLocalValue(newValue);
                  onKeyUp?.(newValue);
                }}
              />
            )}

            {type == "textarea" && (
              <textarea
                ref={inputRef}
                value={localValue}
                placeholder={placeholder}
                rows={rows}
                onChange={(e) => onInputChange(e?.target?.value)}
                onBlur={onInputBlur}
                onKeyUp={localOnKeyUp}
              />
            )}

            {type == "datetime" && (
              <input
                ref={inputRef}
                value={localValue}
                type="datetime-local"
                onChange={(e) => {
                  let newValue = moment(e.target.value).format(
                    "YYYY-MM-DD HH:mm:ss"
                  );
                  setLocalValue(newValue);
                  onChange?.(newValue);
                }}
                onBlur={onInputBlur}
                onKeyUp={localOnKeyUp}
              />
            )}

            {type == "date" && (
              <input
                ref={inputRef}
                value={localValue}
                type="date"
                onChange={(e) => onInputChange(e?.target?.value)}
                onBlur={onInputBlur}
                onKeyUp={localOnKeyUp}
              />
            )}

            {type == "chips" && (
              <div ref={inputRef} className={`chips ${rows ? `c${rows}` : ""}`}>
                {options?.map((option) => {
                  if (typeof option == "string") {
                    option = {
                      value: option,
                      label: option,
                    };
                  }
                  let optionValue =
                    "value" in option ? option.value : option.id;
                  let optionLabel =
                    "label" in option ? option.label : option.name;
                  return (
                    <div
                      key={uid()}
                      className={`chip ${
                        localValue == optionValue ? "active" : ""
                      } ${(optionLabel?.length || 0) > 30 ? "double" : ""}`}
                      onClick={() => {
                        if (localValue == optionValue) {
                          setLocalValue(null);
                          onChange?.(null);
                        } else {
                          setLocalValue(optionValue);
                          onChange?.(optionValue);
                        }
                      }}
                    >
                      {optionLabel}
                    </div>
                  );
                })}
              </div>
            )}
            {type == "chips-multiple" && (
              <div className={`chips ${rows ? `c${rows}` : ""}`}>
                {options?.map((option) => {
                  if (typeof option == "string") {
                    option = {
                      value: option,
                      label: option,
                    };
                  }
                  let optionValue =
                    "value" in option ? option.value : option.id;
                  let optionLabel =
                    "label" in option ? option.label : option.name;
                  return (
                    <div
                      key={uid()}
                      className={`chip ${
                        localValue?.includes(optionValue || "") ? "active" : ""
                      } ${(optionLabel?.length || 0) > 20 ? "double" : ""}`}
                      onClick={() => {
                        localValue ||= [];
                        let newValue = localValue.find(
                          (val: any) => val == optionValue
                        )
                          ? localValue.filter((val: any) => val != optionValue)
                          : [...localValue, optionValue];
                        setLocalValue(newValue);
                        onChange?.(newValue);
                      }}
                    >
                      {optionLabel}
                    </div>
                  );
                })}
              </div>
            )}

            {type == "boolean" && (
              <div className={`chips ${rows ? `c${rows}` : ""}`}>
                {[
                  { value: "true", label: "Oui" },
                  { value: "false", label: "Non" },
                ]?.map((option) => {
                  return (
                    <div
                      key={uid()}
                      className={`chip ${
                        localValue == option.value ? "active" : ""
                      }`}
                      onClick={() => {
                        setLocalValue(option.value);
                        onChange?.(option.value);
                      }}
                    >
                      {option.label}
                    </div>
                  );
                })}
              </div>
            )}
            {unitName && <div className="fieldUnit">{unitName}</div>}
          </div>
        </div>
      </AiEditWrapper>
    </InputChecker>
  );
});

export const DialogSelect = forwardRef<
  HTMLDivElement,
  {
    current: any;
    options: { value: any; label: string | null | undefined }[];
    gridCount: number;
    onChange: (newValue: any) => void;
  }
>(function DialogSelect({ current, options, gridCount, onChange }, ref) {
  const [value, setValue] = useState(current);
  function localOnChange(newValue: any) {
    setValue(newValue);
    onChange(newValue);
  }

  return (
    <div
      ref={ref}
      className="input relative"
      onClick={async () => {
        await openDialog({
          id: "selectDialog",
          title: "Selectionner",
          content: (
            <div
              className="grid gap10"
              style={{
                gridTemplateColumns: `repeat(${gridCount || 3}, 1fr)`,
              }}
            >
              {options.map((option) => {
                return (
                  <div
                    key={uid()}
                    className={`card tcenter ${
                      value == option.value ? "active" : ""
                    }`}
                    onClick={() => {
                      localOnChange(option.value);
                      closeDialog("selectDialog");
                    }}
                  >
                    {option.label || ""}
                  </div>
                );
              })}
            </div>
          ),
        });
      }}
    >
      {value || "Choisir une valeur"}
      {value != null && (
        <CornerIconButton
          type="delete"
          onClick={() => {
            localOnChange(null);
          }}
        ></CornerIconButton>
      )}
    </div>
  );
});
